import { AuthError, InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from '@azure/msal-react';
import { FullStory, isInitialized as isFullstoryInitialized } from '@fullstory/browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import analytics from '../services/analytics';
import {
  clearInteractiveLoginCooldown,
  isInteractiveLoginOnCooldown,
  markInteractiveLoginStarted,
  useAuthToken
} from './entraAuthConfig';
import { signInToAmp } from './signInToAmp';

const authenticationRequest = {
  scopes: ['User.Read'],
  forceRefresh: true
};

const REFRESH_TOKEN_QUERY_KEY = ['msal-auth-refresh-token-query'];
const REFRESH_TOKEN_INTERVAL_MS = 1000 * 60 * 30;
const NETWORK_AUTH_ERROR_CODES = [
  'no_network_connectivity',
  'network_error',
  'post_request_failed',
  'get_request_failed'
];
const TRANSIENT_REFRESH_ERROR_CODES = [
  ...NETWORK_AUTH_ERROR_CODES,
  'user_cancelled',
  'user_canceled',
  'timed_out',
  'empty_window_error',
  'iframe_closed_prematurely',
  'block_iframe_reload',
  'interaction_in_progress',
  'native_handshake_timeout'
];
const SILENT_RETRY_DELAYS_MS = [1000 * 3, 1000 * 10];
const SIGN_IN_TIMEOUT_MS = 1000 * 20;

const isAuthError = (error: unknown): error is AuthError => error instanceof AuthError;
const isNetworkAuthError = (error: AuthError) => NETWORK_AUTH_ERROR_CODES.includes(error.errorCode);
const isTransientRefreshError = (error: AuthError) => TRANSIENT_REFRESH_ERROR_CODES.includes(error.errorCode);

const isDocumentVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

const runWhenDocumentVisible = (callback: () => void) => {
  if (isDocumentVisible()) {
    callback();
    return undefined;
  }

  const onVisibilityChange = () => {
    if (isDocumentVisible()) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      callback();
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => document.removeEventListener('visibilitychange', onVisibilityChange);
};

const useRefreshTokenEveryHalfHourQuery = ({
  acquireToken,
  enabled
}: {
  acquireToken: ReturnType<typeof useMsalAuthentication>['acquireToken'];
  enabled: boolean;
}) =>
  useQuery({
    queryKey: REFRESH_TOKEN_QUERY_KEY,
    queryFn: () => acquireToken(InteractionType.Silent, authenticationRequest),
    refetchInterval: REFRESH_TOKEN_INTERVAL_MS,
    refetchIntervalInBackground: true,
    staleTime: REFRESH_TOKEN_INTERVAL_MS,
    initialData: () => null as Awaited<ReturnType<typeof acquireToken>>,
    throwOnError: false,
    enabled
  });

const EntraUserAuthenticationState = ({
  isAuthenticatedToBackend,
  setIsAuthenticatedToBackend
}: {
  isAuthenticatedToBackend: boolean;
  setIsAuthenticatedToBackend: (isAuthenticatedToBackend: boolean) => void;
}) => {
  const { instance } = useMsal();
  const queryClient = useQueryClient();
  const [authResultFallback, setAuthResultFallback] = React.useState<Awaited<ReturnType<typeof acquireToken>>>(null);

  const navigate = useNavigate();

  const accountIdentifiers = React.useMemo(() => {
    const userEmailFromStorage = (JSON.parse(localStorage.getItem('authInfo') || '{}') as any).currentUserEmail as
      | string
      | undefined;

    if (userEmailFromStorage) {
      return {
        username: userEmailFromStorage
      };
    }
  }, []);

  // arguments MUST be stable values, see hook's implementation
  const { login, result, error, acquireToken } = useMsalAuthentication(
    InteractionType.Silent,
    authenticationRequest,
    accountIdentifiers
  );

  const { error: errorFromQuery } = useRefreshTokenEveryHalfHourQuery({
    acquireToken,
    enabled: isAuthenticatedToBackend
  });

  const location = useLocation();
  const navigateRef = React.useRef(navigate);
  const locationRef = React.useRef(location);
  const interactiveLoginStarted = React.useRef(false);
  const silentRetryCount = React.useRef(0);
  const silentRetryPending = React.useRef(false);
  const backendSignInPending = React.useRef(false);

  React.useEffect(() => {
    navigateRef.current = navigate;
    locationRef.current = location;
  }, [navigate, location]);

  const navigateToLogin = React.useCallback(() => {
    navigateRef.current('/login', {
      state: {
        from: `${locationRef.current.pathname}${locationRef.current.search}`,
        interactiveAuthFailed: true
      }
    });
  }, []);

  React.useEffect(() => {
    const derivedAuthResponse = result || authResultFallback;

    if (derivedAuthResponse) {
      instance.setActiveAccount(derivedAuthResponse.account);
      useAuthToken.setState({ idToken: derivedAuthResponse.idToken, provider: 'entra' });
      localStorage.setItem('entraIdToken', derivedAuthResponse.idToken);
    }

    if (derivedAuthResponse && !isAuthenticatedToBackend && !backendSignInPending.current) {
      backendSignInPending.current = true;
      signInToAmp(setIsAuthenticatedToBackend).then(signedIn => {
        backendSignInPending.current = false;

        if (!signedIn) {
          navigateToLogin();
        }
      });
    }
  }, [instance, authResultFallback, result, isAuthenticatedToBackend, setIsAuthenticatedToBackend, navigateToLogin]);

  const startInteractiveLogin = React.useCallback(
    (derivedError: AuthError) => {
      if (interactiveLoginStarted.current) {
        return;
      }

      interactiveLoginStarted.current = true;
      markInteractiveLoginStarted();

      // eslint-disable-next-line no-console
      console.error('AuthError', derivedError);
      isFullstoryInitialized() && FullStory('trackEvent', { name: 'AuthError', properties: { error: derivedError } });

      login(InteractionType.Popup, {
        ...authenticationRequest,
        prompt: 'select_account'
      })
        .then(loginResult => {
          // eslint-disable-next-line no-console
          console.log('Authentication successful');
          interactiveLoginStarted.current = false;
          clearInteractiveLoginCooldown();
          queryClient.resetQueries({ queryKey: REFRESH_TOKEN_QUERY_KEY });
          setAuthResultFallback(loginResult);
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Authentication failed', err);
          interactiveLoginStarted.current = false;
          setIsAuthenticatedToBackend(false);
          navigateToLogin();
        });
    },
    [login, queryClient, setIsAuthenticatedToBackend, navigateToLogin]
  );

  React.useEffect(() => {
    if (isAuthenticatedToBackend || !isAuthError(error) || interactiveLoginStarted.current) {
      return;
    }

    if (isNetworkAuthError(error)) {
      const retryDelay = SILENT_RETRY_DELAYS_MS[silentRetryCount.current];

      if (retryDelay === undefined) {
        navigateToLogin();
        return;
      }

      silentRetryCount.current += 1;
      silentRetryPending.current = true;

      const timeoutId = window.setTimeout(() => {
        silentRetryPending.current = false;

        const retry =
          instance.getAllAccounts().length > 0
            ? acquireToken(InteractionType.Silent, authenticationRequest)
            : login(InteractionType.Silent, authenticationRequest);

        retry.catch(() => undefined);
      }, retryDelay);

      return () => {
        window.clearTimeout(timeoutId);
        silentRetryPending.current = false;
      };
    }

    if (isInteractiveLoginOnCooldown()) {
      navigateToLogin();
      return;
    }

    return runWhenDocumentVisible(() => startInteractiveLogin(error));
  }, [isAuthenticatedToBackend, error, instance, acquireToken, login, startInteractiveLogin, navigateToLogin]);

  React.useEffect(() => {
    if (
      !isAuthenticatedToBackend ||
      !isAuthError(errorFromQuery) ||
      isTransientRefreshError(errorFromQuery) ||
      interactiveLoginStarted.current ||
      isInteractiveLoginOnCooldown()
    ) {
      return;
    }

    return runWhenDocumentVisible(() => startInteractiveLogin(errorFromQuery));
  }, [isAuthenticatedToBackend, errorFromQuery, startInteractiveLogin]);

  React.useEffect(() => {
    if (isAuthenticatedToBackend) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (interactiveLoginStarted.current || silentRetryPending.current || backendSignInPending.current) {
        return;
      }

      navigateToLogin();
    }, SIGN_IN_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isAuthenticatedToBackend, navigateToLogin]);

  React.useEffect(() => {
    analytics.page(location);
  }, [location]);

  return <Outlet />;
};

export default EntraUserAuthenticationState;
